import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Pencil, Trash2, Link2, Filter } from 'lucide-react';
import { toast } from 'react-toastify';
import Header from '../../components/Header';
import CustomTable from '../../components/CustomTable';
import { greenColor } from '../../config/colors';
import { useCookiesLogin } from '../../config/cookies';
import api from '../../services/api';
import SimulatorFormModal from './components/SimulatorFormModal';
import LinkedResalesModal from './components/LinkedResalesModal';
import FilterModal from './components/FilterModal';
import DeleteModal from './components/DeleteModal';
import { formatMoney as formatCurrency } from '../../utils/index';
import { useTranslation } from '../../hooks/useTranslation';

const SimulatorSuperAdmin = () => {
  const { cookies } = useCookiesLogin();
  const { t } = useTranslation();

  const [simulators, setSimulators] = useState([]);
  const [resales, setResales] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedResaleId, setSelectedResaleId] = useState(null);

  const [formModal, setFormModal] = useState({ open: false, initial: null });
  const [linkedModal, setLinkedModal] = useState({ open: false, simulator: null });
  const [filterModal, setFilterModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, simulator: null });

  const fetchSimulators = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data } = await api.get('/v1/simulators');
      const list = data?.data ?? [];
      setSimulators(list);
    } catch {
      toast.error(t('simulatorSuperAdmin.toasts.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const fetchResales = useCallback(async () => {
    try {
      const { data } = await api.get('/v1/company');
      setResales(data?.data ?? []);
    } catch {
      toast.error(t('simulatorSuperAdmin.toasts.resalesLoadError'));
    }
  }, [t]);

  useEffect(() => {
    if (!cookies?.AUTH_TOKEN) return;
    fetchSimulators();
    fetchResales();
  }, [cookies?.AUTH_TOKEN, fetchSimulators, fetchResales]);

  const filteredSimulators = useMemo(() => {
    let result = simulators;
    if (selectedResaleId) {
      result = result.filter((s) => s.resale_ids?.includes(selectedResaleId));
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((s) => s.name?.toLowerCase().includes(q));
    }
    return result;
  }, [simulators, search, selectedResaleId]);

  const handleClearSearch = () => {
    setSearch('');
    setSelectedResaleId(null);
  };

  const handleDelete = async () => {
    const { simulator } = deleteModal;
    if (!simulator) return;
    try {
      await api.delete(`/v1/simulators/${simulator.id}`);
      toast.success(t('simulatorSuperAdmin.toasts.deleteSuccess'));
      setDeleteModal({ open: false, simulator: null });
      fetchSimulators();
    } catch {
      toast.error(t('simulatorSuperAdmin.toasts.deleteError'));
    }
  };

  const columns = [
    { field: 'name', headerName: t('simulatorSuperAdmin.columns.name') },
    {
      field: 'interest_rate',
      headerName: t('simulatorSuperAdmin.columns.interestRate'),
      render: (v) => `${v}%`,
    },
    {
      field: 'min_value',
      headerName: t('simulatorSuperAdmin.columns.minValue'),
      render: (v) => formatCurrency(v),
    },
    {
      field: 'max_value',
      headerName: t('simulatorSuperAdmin.columns.maxValue'),
      render: (v) => formatCurrency(v),
    },
  ];

  const actions = [
    {
      tooltip: t('simulatorSuperAdmin.actions.linkedResales'),
      icon: <Link2 size={16} />,
      color: '#E5F6D3',
      onClick: (row) => setLinkedModal({ open: true, simulator: row }),
    },
    {
      tooltip: t('common.edit'),
      icon: <Pencil size={16} />,
      color: '#FEF9C3',
      onClick: (row) => setFormModal({ open: true, initial: row }),
    },
    {
      tooltip: t('common.delete'),
      icon: <Trash2 size={16} color="#DC2626" />,
      color: '#FEE2E2',
      onClick: (row) => setDeleteModal({ open: true, simulator: row }),
    },
  ];

  if (!cookies.AUTH_TOKEN || isLoading) {
    return (
      <Backdrop open style={{ zIndex: 9999 }}>
        <CircularProgress sx={{ color: greenColor }} />
      </Backdrop>
    );
  }

  return (
    <Box m="20px">
      <Header
        title={t('simulatorSuperAdmin.title')}
        subtitle={t('simulatorSuperAdmin.subtitle')}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <TextField
          placeholder={t('forms.searchByName')}
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, maxWidth: 340 }}
        />

        <Button
          variant="contained"
          onClick={handleClearSearch}
          sx={{
            bgcolor: greenColor,
            color: '#000',
            '&:hover': { bgcolor: '#6ec13b' },
            fontWeight: 700,
          }}
        >
          {t('common.clear').toUpperCase()}
        </Button>

        <Tooltip title={t('common.filter')}>
          <IconButton
            onClick={() => setFilterModal(true)}
            sx={{
              bgcolor: greenColor,
              color: '#000',
              borderRadius: '8px',
              '&:hover': { bgcolor: '#6ec13b' },
            }}
          >
            <Filter size={18} />
          </IconButton>
        </Tooltip>

        <Box flex={1} />

        <Button
          variant="contained"
          onClick={() => setFormModal({ open: true, initial: null })}
          sx={{
            bgcolor: greenColor,
            color: '#000',
            '&:hover': { bgcolor: '#6ec13b' },
            whiteSpace: 'nowrap',
            fontWeight: 700,
          }}
        >
          {t('simulatorSuperAdmin.newSimulator')}
        </Button>
      </Box>

      {/* ── Tabela ── */}
      <Box
        sx={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          px: '20px',
          pb: '4px',
        }}
      >
        <CustomTable
          columns={columns}
          rows={filteredSimulators}
          rowKey="id"
          actions={actions}
          emptyMessage={t('simulatorSuperAdmin.noSimulatorFound')}
          rowsPerPageOptions={[5, 10, 15]}
          elevation={0}
        />
      </Box>

      {/* ── Modais ── */}
      <SimulatorFormModal
        open={formModal.open}
        onClose={() => setFormModal({ open: false, initial: null })}
        onSaved={fetchSimulators}
        initial={formModal.initial}
      />
      <LinkedResalesModal
        open={linkedModal.open}
        onClose={() => setLinkedModal({ open: false, simulator: null })}
        simulator={linkedModal.simulator}
        allResales={resales}
      />
      <FilterModal
        open={filterModal}
        onClose={() => setFilterModal(false)}
        onApply={({ resale_id } = {}) => setSelectedResaleId(resale_id ?? null)}
        resales={resales}
      />
      <DeleteModal
        open={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, simulator: null })}
        onConfirm={handleDelete}
        simulator={deleteModal.simulator}
      />
    </Box>
  );
};

export default SimulatorSuperAdmin;
